/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains utilities to streamline the creation of requests.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Typing
 */
import { JsonServiceClient } from '@servicestack/client';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../api/dtos';

/**
 * Used to replace the return type of a function.
 */
import { ReplaceReturnType } from './functionTypes';

/**
 * Used to make API calls to the servicestack API.
 */
import client from 'src/api/client';


/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

type RequestCreatorFunction<P> = (...args: any[]) => Partial<P>;

type ClientRequestFunction<T, R> = (client: JsonServiceClient, request: T) => Promise<R>;

type DtoReturnType<T> = T extends Dtos.IReturn<infer S> ? S : never;

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/**
 * This method creates a request to the ServiceStack API. This was made because
 * ServiceStack typescript DTO class definitions didn't support optional params.
 * 
 * @augments {JsonServiceClient}
 * @param type The DTO Type
 * @param requestMapping The mapping of function parameters to DTO properties
 * @returns observable request.
 */
const createRequest = <
    S,
    T extends Dtos.IReturn<S>,
    FunctionType extends RequestCreatorFunction<T>,
    U extends ReplaceReturnType<FunctionType, Promise<DtoReturnType<T>>>
>(type: new() => T, requestMapping: FunctionType, clientRequest: ClientRequestFunction<T, S>): U => {
    const fn: any = (...args: any[]) => {
        const request: T = new type();

        const values = requestMapping(...args)

        for (const key of Object.keys(values)) {
            (request as any)[key] = values[key];
        }

        return clientRequest(client, request);
    };

    return fn;
};


/*
 * ---------------------------------------------------------------------------------
 * Exports
 * ---------------------------------------------------------------------------------
 */

/**
 * This method creates a post request to the ServiceStack API. This was made because
 * ServiceStack typescript DTO class definitions didn't support optional params.
 * 
 * @augments JsonServiceClient 
 * @param type The DTO Type
 * @param requestMapping The mapping of function parameters to DTO properties
 * @returns observable request.
 */
export const createPostRequest = <
    S,
    T extends Dtos.IReturn<S>,
    FunctionType extends RequestCreatorFunction<T>,
    U extends ReplaceReturnType<FunctionType, Promise<DtoReturnType<T>>>
>(type: new() => T, requestMapping: FunctionType): U => {
    return createRequest(type, requestMapping, (apiClient, request) => apiClient.post(request))
}

/**
 * This method creates a get request to the ServiceStack API. This was made because
 * ServiceStack typescript DTO class definitions didn't support optional params.
 * 
 * @augments {JsonServiceClient}
 * @param type The DTO Type
 * @param requestMapping The mapping of function parameters to DTO properties
 * @returns observable request.
 */
export const createGetRequest = <
    S,
    T extends Dtos.IReturn<S>,
    FunctionType extends RequestCreatorFunction<T>,
    U extends ReplaceReturnType<FunctionType, Promise<DtoReturnType<T>>>
>(type: new() => T, requestMapping: FunctionType): U => {
    return createRequest(type, requestMapping, (apiClient, request) => {
        return apiClient.get(request)
    })
}

/**
 * This method creates a get request to the ServiceStack API. This was made because
 * ServiceStack typescript DTO class definitions didn't support optional params.
 * 
 * @augments {JsonServiceClient}
 * @param type The DTO Type
 * @param requestMapping The mapping of function parameters to DTO properties
 * @returns observable request.
 */
export const createPutRequest = <
    S,
    T extends Dtos.IReturn<S>,
    FunctionType extends RequestCreatorFunction<T>,
    U extends ReplaceReturnType<FunctionType, Promise<DtoReturnType<T>>>
>(type: new() => T, requestMapping: FunctionType): U => {
    return createRequest(type, requestMapping, (apiClient, request) => {
        return apiClient.put(request)
    })
}

/**
 * This method creates a get request to the ServiceStack API. This was made because
 * ServiceStack typescript DTO class definitions didn't support optional params.
 * 
 * @augments {JsonServiceClient}
 * @param type The DTO Type
 * @param requestMapping The mapping of function parameters to DTO properties
 * @returns observable request.
 */
export const createDeleteRequest = <
    S,
    T extends Dtos.IReturn<S>,
    FunctionType extends RequestCreatorFunction<T>,
    U extends ReplaceReturnType<FunctionType, Promise<DtoReturnType<T>>>
>(type: new() => T, requestMapping: FunctionType): U => {
    return createRequest(type, requestMapping, (apiClient, request) => {
        return apiClient.delete(request)
    })
}


// WEBPACK FOOTER //
// ./src/utils/createRequest.ts